import { useCallback, useState, type ReactNode } from 'react';
import { KeyRound, Terminal } from 'lucide-react';
import { SubTabs, TabPanel, type TabDefinition } from '@/components/ui';
import { EnrollmentInstructions } from '@/components/nodes/EnrollmentInstructions';
import { SshInstall } from '@/components/nodes/SshInstall';
import type { EnrollmentIssued } from '@/types/api';

type Way = 'ssh' | 'manual';

const WAYS: readonly TabDefinition<Way>[] = [
  { id: 'ssh', label: 'Установить по SSH', icon: KeyRound },
  { id: 'manual', label: 'Команда вручную', icon: Terminal },
];

const PREFIX = 'agent-setup-';

/**
 * Putting an agent on a server, two ways (ADR-0036): the panel logs in over
 * SSH once and installs it, or the operator runs the command. A failed SSH
 * installation hands over a fresh token, and the manual way shows it — the
 * one issued first no longer works by then.
 */
export function AgentSetup({ issued }: { readonly issued: EnrollmentIssued }): ReactNode {
  const [way, setWay] = useState<Way>('ssh');
  const [current, setCurrent] = useState(issued);
  const onFallback = useCallback((fresh: EnrollmentIssued) => setCurrent(fresh), []);

  return (
    <div className="stack">
      <div>
        <SubTabs tabs={WAYS} active={way} onChange={setWay} label="Как установить агента" prefix={PREFIX} />
      </div>
      {/* Both stay mounted: switching to the command must not lose an installation in progress. */}
      <div hidden={way !== 'ssh'}>
        <SshInstall issued={issued} onFallback={onFallback} />
      </div>
      <TabPanel id="manual" active={way} prefix={PREFIX}>
        <EnrollmentInstructions issued={current} />
      </TabPanel>
    </div>
  );
}
